import { useQuery } from '@tanstack/react-query';
import { Compass, RefreshCw } from 'lucide-react';
import { getEventJourney } from '@/api/event-journey';
import { getEvent } from '@/api/events';
import { useAuth } from '@/context/AuthContext';

/** Test surface for the server contract, not the final consumer design. */
export function EventJourneyPanel({ eventId }: { eventId: string }) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ['event-journey', eventId, user?.id ?? 'anonymous'],
    queryFn: () => getEventJourney(eventId), staleTime: 0, gcTime: 0,
    refetchOnWindowFocus: 'always',
    refetchInterval: (q) => {
      const refresh = q.state.data?.data.refresh;
      const boundary = refresh?.nextTransitionAt ? new Date(refresh.nextTransitionAt).getTime() - Date.now() : Infinity;
      return Math.max(1000, Math.min(30_000, boundary));
    },
  });
  const data = query.data?.data;
  // Belonging and the host, from the event detail (R28, R31).
  const detail = useQuery({ queryKey: ['event-detail', eventId, user?.id ?? 'anonymous'], queryFn: () => getEvent(eventId) }).data?.data;
  return <section aria-labelledby="event-journey-title" className="rounded-xl border border-gray-200 p-4 space-y-3">
    <div className="flex items-center justify-between gap-3">
      <h3 id="event-journey-title" className="flex items-center gap-2 font-semibold"><Compass size={18} aria-hidden="true" />Event journey preview</h3>
      <button type="button" className="btn-secondary flex items-center gap-2" onClick={() => void query.refetch()} disabled={query.isFetching}>
        <RefreshCw size={14} aria-hidden="true" />Refresh
      </button>
    </div>
    <p className="text-sm text-gray-600">Read-only preview for the signed-in account. Actions below are contract labels, not publish or booking controls.</p>
    {query.isPending && <p role="status">Loading the current journey…</p>}
    {query.isError && <p role="alert">Could not refresh the journey. Do not act on an older result.</p>}
    {data && !query.isError && <>
      <p className="text-sm font-medium">Event: {data.timing.phase} · You: {data.viewer.state}</p>
      <div className="divide-y divide-gray-200">
        {data.decision.primaryFacts.map((fact) => <div key={fact.code} className="py-2">
          <p className="font-medium">{fact.label}</p>
          <p className="text-xs text-gray-500">{fact.confirmedAt ? 'Organizer confirmed' : fact.source === 'visible_match_rsvps' ? 'Visible going RSVPs from strong matches' : 'From event details'}</p>
        </div>)}
      </div>
      {data.decision.supportingFacts.length > 0 && <details><summary className="text-sm cursor-pointer">More event facts</summary>
        <ul className="text-sm mt-2 space-y-1">{data.decision.supportingFacts.map((fact) => <li key={fact.code}>{fact.label}</li>)}</ul>
      </details>}
      {data.decision.warnings.map((warning) => <p className="text-sm text-gray-600" key={warning}>{warning}</p>)}
      <p className="text-sm">{data.arrival.isVirtual ? 'Online event' : [data.arrival.venueName, data.arrival.address, data.arrival.city, data.arrival.state].filter(Boolean).join(' · ') || 'Venue not listed'}</p>
      <div className="pt-2 border-t border-gray-200">
        {data.primaryAction.cardOnly
          ? <p className="font-semibold">Nothing to do on this page (card only: {data.primaryAction.label})</p>
          : <p className="font-semibold">Next action: {data.primaryAction.label}</p>}
        <p className="text-xs text-gray-500">{data.actionReason} · {data.primaryAction.code}{data.primaryAction.requiresAuth ? ' · Sign-in required' : ''}</p>
      </div>
      {detail && <p className="text-xs text-gray-500">
        Matches going: {detail.matchesGoingCount ?? 0} · Going who share your roots: {detail.sharedRootsCount ?? 'not shown (signed out, no state roots, or under 3)'}
        {detail.organizer?.eventCount !== undefined && <> · Host: {detail.organizer.eventCount} events, on WeDeshi since {detail.organizer.since?.slice(0, 10)}</>}
      </p>}
    </>}
  </section>;
}
